import { z } from 'zod';
// ============================================================
// Studio Chatbot — 사용자 문의 챗봇
// ============================================================
/** Anthropic Messages API 호환 content block */
export const ChatbotContentBlockSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('text'), text: z.string() }),
    z.object({
        type: z.literal('tool_use'),
        id: z.string(),
        name: z.string(),
        input: z.record(z.string(), z.unknown()),
    }),
    z.object({
        type: z.literal('tool_result'),
        tool_use_id: z.string(),
        content: z.string(),
        is_error: z.boolean().optional(),
    }),
]);
/**
 * 직렬화된 thinking 블록 텍스트 판별 (FIKET-429).
 * 2026-09-28 이전 저장 코드는 모델의 thinking/redacted_thinking 블록을 JSON 문자열로 text 블록에 넣었다.
 * 이제는 저장하지 않지만, 이미 저장된 행이 고객·어드민 화면과 미리보기에 노출되지 않도록 표시 측에서 걸러낸다.
 */
const SERIALIZED_THINKING_PREFIXES = ['{"type":"thinking"', '{"type":"redacted_thinking"'];
export function isSerializedThinkingText(text) {
    const head = text.trimStart();
    return SERIALIZED_THINKING_PREFIXES.some((p) => head.startsWith(p));
}
/** 사용자에게 보여줄 text 블록만 — 직렬화된 thinking 제외 */
export function displayTextBlocks(blocks) {
    return (blocks ?? []).filter((b) => b.type === 'text' && !isSerializedThinkingText(b.text));
}
export const ChatbotMessageRoleSchema = z.enum(['user', 'assistant']);
/** 챗봇 모델 티어 — 실제 ID 는 서버 provider 매핑(anthropic-client.ts)이 결정 */
export const ChatbotModelTierSchema = z.enum(['haiku', 'sonnet', 'opus']);
export const CHATBOT_MODEL_TIER_LABELS = {
    haiku: 'Haiku (저비용·빠름)',
    sonnet: 'Sonnet (기본)',
    opus: 'Opus (최고 품질·느림)',
};
/** 세션 출처 — 'admin_playground' 는 어드민 테스트 대화(KPI·목록 기본 제외) */
export const ChatbotSessionSourceSchema = z.enum(['web', 'admin_playground']);
// 클라이언트 → 서버 메시지 전송
export const SendChatbotMessageRequestSchema = z.object({
    sessionId: z.string().cuid2().nullable(),
    message: z.string().min(1, '메시지를 입력하세요').max(2000, '메시지는 2000자를 초과할 수 없습니다'),
    /** 어드민 테스트 콘솔 전용 — 요청 단위 모델 티어 덮어쓰기. ADMIN 이 아니면 서버가 무시한다 */
    modelTier: ChatbotModelTierSchema.optional(),
    /** 어드민 테스트 콘솔 전용 — 새 세션 출처 표시. ADMIN 이 아니면 서버가 무시한다 */
    source: ChatbotSessionSourceSchema.optional(),
});
// FAQ 칩 — 위젯이 보여줄 공개 FAQ 목록 (질문만; 답변은 서버가 보관)
export const ChatbotFaqChipSchema = z.object({
    id: z.string(),
    category: z.string(),
    question: z.string(),
});
// FAQ 칩 탭 — LLM 우회. faqId 만 보내면 서버가 저장된 질문·답변을 즉시 반환한다.
// 정해진 질문엔 정해진 답변 → provider 과부하(overloaded_error) 영향 없음, 비용 0.
export const SendChatbotFaqRequestSchema = z.object({
    sessionId: z.string().cuid2().nullable(),
    faqId: z.string().min(1),
});
export const SendChatbotFaqResponseSchema = z.object({
    sessionId: z.string(),
    question: z.string(),
    answer: z.string(),
});
// 클라이언트가 받는 메시지(휘발성 표시용 — 텍스트 + tool_use 메타만)
export const ChatbotDisplayMessageSchema = z.object({
    id: z.string(),
    role: ChatbotMessageRoleSchema,
    text: z.string(),
    toolCalls: z
        .array(z.object({ name: z.string(), summary: z.string().optional() }))
        .optional(),
    createdAt: z.string(),
});
/**
 * 세션 복원 시 히스토리 메시지 role.
 * agent/system 은 레거시 라이브 채팅(폐기됨) 세션에 남아 있는 행 — 읽기 전용 렌더용.
 */
export const ChatbotHistoryMessageRoleSchema = z.enum(['user', 'assistant', 'agent', 'system']);
/** 세션 복원 응답에서 클라이언트가 보는 단일 메시지 모양. */
export const ChatbotHistoryMessageSchema = z.object({
    id: z.string(),
    role: ChatbotHistoryMessageRoleSchema,
    text: z.string(),
    createdAt: z.string(),
});
// ============================================================
// 사용자 세션 목록·복원 — 위젯이 이전 대화방을 다시 열어보기 위해 사용
// ============================================================
/** 위젯의 세션 목록에서 한 줄로 보여줄 카드 정보. */
export const ChatbotSessionListItemSchema = z.object({
    id: z.string(),
    startedAt: z.string(),
    endedAt: z.string().nullable(),
    messageCount: z.number().int(),
    /** 첫 사용자 메시지 일부 — 카드 제목으로 사용. 없으면 빈 문자열. */
    firstUserMessage: z.string(),
    /** 마지막 메시지(role 무관) 일부 — 미리보기 한 줄. */
    lastMessagePreview: z.string(),
});
export const ChatbotSessionListResponseSchema = z.object({
    sessions: z.array(ChatbotSessionListItemSchema),
});
// ============================================================
// 확인 카드 액션 (FIKET-424) — write tool 은 "확인 요청"만 만들고, 사용자가 카드에서 확인하면
// 서버가 자격을 재검증한 뒤 실행한다. LLM 은 실행 여부를 결정하지 않는다.
// 환불은 포함하지 않는다(2026-10-06 결정: 금전·비가역 액션은 결제 내역 화면의 환불 모달에서만).
// ============================================================
export const ChatbotActionTypeSchema = z.enum(['redeem_activation_code', 'cancel_ai_job']);
export const CHATBOT_ACTION_TYPE_LABELS = {
    redeem_activation_code: '활성화 코드 등록',
    cancel_ai_job: 'AI 작업 취소',
};
/**
 * pending → (confirm) executing → executed | failed
 *         → (cancel)  cancelled
 *         → (만료, lazy) expired
 *         → (confirm 시 재검증 불일치) rejected_mismatch
 */
export const ChatbotPendingActionStatusSchema = z.enum([
    'pending',
    'executing',
    'executed',
    'cancelled',
    'expired',
    'rejected_mismatch',
    'failed',
]);
export const CHATBOT_PENDING_ACTION_STATUS_LABELS = {
    pending: '확인 대기',
    executing: '실행 중',
    executed: '실행 완료',
    cancelled: '취소됨',
    expired: '만료됨',
    rejected_mismatch: '조건 변경으로 거절',
    failed: '실행 실패',
};
/** 카드에 그릴 요약 — 서버가 제안 시점 값으로 만든다(스냅샷). 모델이 만들지 않는다. */
export const ChatbotActionSummarySchema = z.object({
    title: z.string(),
    lines: z.array(z.object({ label: z.string(), value: z.string() })),
    /** 금액(원) — 금전이 걸린 액션일 때. 없으면 null */
    amount: z.number().int().nullable(),
    /** 카드 하단 주의 문구 */
    note: z.string().nullable(),
});
export const ChatbotPendingActionSchema = z.object({
    id: z.string(),
    sessionId: z.string(),
    type: ChatbotActionTypeSchema,
    status: ChatbotPendingActionStatusSchema,
    summary: ChatbotActionSummarySchema,
    expiresAt: z.string(),
    createdAt: z.string(),
    executedAt: z.string().nullable(),
    result: z.record(z.string(), z.unknown()).nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
});
/** POST /api/chatbot/actions/:id/confirm body — 현재 액션들은 추가 입력이 없다(빈 객체). 액션별 입력이 생기면 optional 로 확장 */
export const ConfirmChatbotActionRequestSchema = z.object({});
export const ConfirmChatbotActionResponseSchema = z.object({
    action: ChatbotPendingActionSchema,
    /** 실행 결과로 세션에 추가된 메시지(시스템 안내 + 챗봇 완료 문구) — 위젯이 그대로 붙인다 */
    messages: z.array(ChatbotHistoryMessageSchema),
});
export const CancelChatbotActionResponseSchema = z.object({
    action: ChatbotPendingActionSchema,
});
/** 어드민 상세용 — 모델 입력(params)과 제안 시점 스냅샷까지 포함 */
export const AdminChatbotPendingActionSchema = ChatbotPendingActionSchema.extend({
    toolUseId: z.string().nullable(),
    params: z.record(z.string(), z.unknown()),
    snapshot: z.record(z.string(), z.unknown()),
});
/** 한 세션의 전체 메시지 + 메타. */
export const ChatbotSessionDetailSchema = z.object({
    id: z.string(),
    startedAt: z.string(),
    endedAt: z.string().nullable(),
    messages: z.array(ChatbotHistoryMessageSchema),
    /** 확인 대기 중인 액션(세션당 최대 1건) — 복원 시 카드 재표시용. 구 클라이언트 호환을 위해 optional */
    pendingAction: ChatbotPendingActionSchema.nullable().optional(),
});
// ============================================================
// Admin: FAQ CRUD
// ============================================================
/**
 * FAQ 상태 (FIKET-431) — active 만 챗봇 지식·칩·작성 전 제안에 쓰인다.
 * pending_review = 답변 완료 문의에서 AI 가 만든 후보(어드민 검토 대기), rejected = 반려.
 * isActive 는 active 상태 안에서의 노출 on/off 토글로 그대로 유지한다.
 */
export const ChatbotFaqStatusSchema = z.enum(['active', 'pending_review', 'rejected']);
export const CHATBOT_FAQ_STATUS_LABELS = {
    active: '활성',
    pending_review: '검토 대기',
    rejected: '반려',
};
export const ChatbotFaqSchema = z.object({
    id: z.string().cuid2(),
    category: z.string().min(1).max(64),
    question: z.string().min(1),
    answer: z.string().min(1),
    sortOrder: z.number().int(),
    isActive: z.boolean(),
    status: ChatbotFaqStatusSchema,
    /** AI 후보의 원 문의(contact_inquiries.id). 수동 등록은 null */
    sourceInquiryId: z.string().nullable(),
    updatedBy: z.string().cuid2().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateChatbotFaqRequestSchema = z.object({
    category: z.string().min(1).max(64),
    question: z.string().min(1).max(500),
    answer: z.string().min(1).max(5000),
    sortOrder: z.number().int().default(0),
    isActive: z.boolean().default(true),
});
// Create.partial() 로 만들지 않는다 — zod v4 에서 default 가 있는 필드는 partial 이어도 기본값이 채워져
// 한 필드만 PATCH 해도 sortOrder/isActive 가 기본값으로 리셋되는 함정(admin-roles.ts 와 동일 회피).
export const UpdateChatbotFaqRequestSchema = z.object({
    category: z.string().min(1).max(64).optional(),
    question: z.string().min(1).max(500).optional(),
    answer: z.string().min(1).max(5000).optional(),
    sortOrder: z.number().int().optional(),
    isActive: z.boolean().optional(),
    /** 후보 승인(active) / 반려(rejected) 도 이 PATCH 로 (FIKET-431) */
    status: ChatbotFaqStatusSchema.optional(),
});
// ============================================================
// Admin: 이탈 세션 공백 리포트 — GET /api/admin/chatbot-sessions/gap-report (FIKET-431)
// 문의 유도(escalation)나 문의 전환으로 끝난 세션의 첫 고객 질문 = "비어 있는 FAQ" 단서. LLM 없이 쿼리만.
// ============================================================
export const ChatbotGapReportItemSchema = z.object({
    sessionId: z.string(),
    startedAt: z.string(),
    /** 첫 사용자 질문(120자) — 없으면 null */
    firstUserMessage: z.string().nullable(),
    escalationSuggestedAt: z.string().nullable(),
    outcome: z.string().nullable(),
    /** 이 세션을 첨부해 생성된 문의 */
    linkedInquiryId: z.string().nullable(),
});
export const ChatbotGapReportSchema = z.object({
    range: z.object({ startDate: z.string().nullable(), endDate: z.string().nullable() }),
    items: z.array(ChatbotGapReportItemSchema),
    /** 조건에 맞는 전체 세션 수(items 는 limit 만큼) */
    total: z.number().int(),
});
// ============================================================
// Admin: Session 조회 (어드민 v2 /v2/chatbot/sessions)
// ============================================================
/** 세션 종결 형태. NULL(미기록) = 안내만으로 종료·이탈 */
export const ChatbotSessionOutcomeSchema = z.enum(['inquiry_created', 'action_executed']);
export const CHATBOT_SESSION_OUTCOME_LABELS = {
    inquiry_created: '문의로 전환',
    action_executed: '액션 실행',
};
export const ChatbotSessionSummarySchema = z.object({
    id: z.string().cuid2(),
    userId: z.string().cuid2(),
    userEmail: z.string().nullable(),
    startedAt: z.string(),
    endedAt: z.string().nullable(),
    messageCount: z.number().int(),
    totalInputTokens: z.number().int(),
    totalOutputTokens: z.number().int(),
    totalCacheReadTokens: z.number().int(),
    totalCacheWriteTokens: z.number().int(),
    // 아래는 어드민 v2 확장 필드 — 구 클라이언트 호환을 위해 optional
    source: ChatbotSessionSourceSchema.optional(),
    userName: z.string().nullable().optional(),
    escalationSuggestedAt: z.string().nullable().optional(),
    outcome: ChatbotSessionOutcomeSchema.nullable().optional(),
    /** 첫 사용자 질문 미리보기(120자) */
    firstUserMessage: z.string().optional(),
    /** 세션에서 호출된 tool 이름(중복 제거) */
    toolNames: z.array(z.string()).optional(),
    /** LLM 오류 placeholder 수 */
    errorCount: z.number().int().optional(),
    /** FAQ 칩 즉답 수 */
    faqAnswerCount: z.number().int().optional(),
    /** 이 세션을 첨부해 생성된 문의 ID(최신 1건) */
    linkedInquiryId: z.string().nullable().optional(),
});
export const AdminChatbotSessionMessageSchema = z.object({
    id: z.string(),
    role: ChatbotHistoryMessageRoleSchema,
    content: z.array(ChatbotContentBlockSchema),
    /** 'faq' = FAQ 칩 즉답, 'error' = LLM 실패 placeholder, 'action' = 확인 카드 실행 결과, 그 외 모델 stop_reason */
    modelStopReason: z.string().nullable(),
    createdAt: z.string(),
});
export const AdminChatbotSessionDetailSchema = z.object({
    session: ChatbotSessionSummarySchema,
    messages: z.array(AdminChatbotSessionMessageSchema),
    /** 확인 카드 액션 이력(최신순) — 신규 필드라 optional */
    actions: z.array(AdminChatbotPendingActionSchema).optional(),
});
// ============================================================
// Admin: 챗봇 설정 (app_settings 'chatbot_settings')
// ============================================================
/** tool 설명문 오버라이드 1건 최대 길이 (FIKET-432) */
export const CHATBOT_TOOL_DESCRIPTION_MAX = 1000;
/**
 * tool 이름 → 모델에 보낼 설명문 덮어쓰기 (FIKET-432).
 * 키가 없거나 빈 문자열이면 코드 기본 설명문. name·input_schema·execute 는 코드 전용으로 바꿀 수 없다.
 */
export const ChatbotToolDescriptionOverridesSchema = z.record(z.string().min(1).max(64), z.string().max(CHATBOT_TOOL_DESCRIPTION_MAX));
export const ChatbotSettingsSchema = z.object({
    /** null = 배포 env(CHATBOT_MODEL_TIER) 기본값 사용 */
    modelTier: ChatbotModelTierSchema.nullable(),
    /** 한 세션에서 사용자가 질문할 수 있는 최대 횟수 */
    maxSessionTurns: z.number().int().min(5).max(200),
    /** 모델에 노출하지 않을 tool 이름(문의 유도 tool 은 비활성화 불가) */
    disabledTools: z.array(z.string().min(1).max(64)).max(50),
    /** 확인 카드 write tool 노출(3단계 이후 의미) */
    writeToolsEnabled: z.boolean(),
    /** tool 설명문 오버라이드(비어 있으면 코드 기본값) */
    toolDescriptionOverrides: ChatbotToolDescriptionOverridesSchema,
});
export const DEFAULT_CHATBOT_SETTINGS = {
    modelTier: null,
    maxSessionTurns: 50,
    disabledTools: [],
    writeToolsEnabled: false,
    toolDescriptionOverrides: {},
};
// Create.partial() 이 아니라 optional 전용(기본값 없음) — 부분 PATCH 가 다른 항목을 덮어쓰지 않게
export const UpdateChatbotSettingsRequestSchema = z.object({
    modelTier: ChatbotModelTierSchema.nullable().optional(),
    maxSessionTurns: z.number().int().min(5).max(200).optional(),
    disabledTools: z.array(z.string().min(1).max(64)).max(50).optional(),
    writeToolsEnabled: z.boolean().optional(),
    /** 전체 맵을 보낸다(부분 병합 아님). 값이 빈 문자열이면 그 tool 은 기본값으로 복원 */
    toolDescriptionOverrides: ChatbotToolDescriptionOverridesSchema.optional(),
});
/** tool 의 Anthropic input_schema (JSON Schema object 부분집합) — 테스트 콘솔 인자 입력 안내용 */
export const ChatbotToolInputSchemaSchema = z.object({
    type: z.literal('object'),
    properties: z.record(z.string(), z.unknown()),
    required: z.array(z.string()).optional(),
});
export const AdminChatbotToolInfoSchema = z.object({
    name: z.string(),
    /** 모델에 실제로 전달되는 설명문(오버라이드 반영) */
    description: z.string(),
    /** 코드에 정의된 기본 설명문 */
    defaultDescription: z.string(),
    /** 저장된 오버라이드(없으면 null) */
    descriptionOverride: z.string().nullable(),
    /** read = 조회, write = 확인 카드 실행(3단계), system = 항상 켜짐(문의 유도) */
    kind: z.enum(['read', 'write', 'system']),
    enabled: z.boolean(),
    inputSchema: ChatbotToolInputSchemaSchema,
});
// ============================================================
// Admin: 테스트 콘솔 tool 직접 실행 — POST /api/admin/chatbot-tools/:name/run (FIKET-432)
// 모델 호출 없이 서버가 대상 사용자 id 로 조회 tool 의 execute 만 돌린다.
// ============================================================
export const RunChatbotToolRequestSchema = z.object({
    /** tool input_schema 에 맞는 인자. 생략하면 {} */
    input: z.record(z.string(), z.unknown()).optional(),
    /** 대상 사용자 id. 생략하면 호출한 관리자 본인 */
    targetUserId: z.string().min(1).max(128).optional(),
});
export const RunChatbotToolResponseSchema = z.object({
    name: z.string(),
    targetUser: z.object({ id: z.string(), email: z.string().nullable(), name: z.string().nullable() }),
    /** execute 가 예외 없이 끝났는지 */
    ok: z.boolean(),
    /** tool_result 로 모델에 들어갈 문자열 그대로(오류면 오류 메시지) */
    result: z.string(),
    elapsedMs: z.number().int(),
    /** 문자 수 기반 추정치(정확한 토크나이저 아님) */
    estimatedTokens: z.number().int(),
});
export const AdminChatbotSettingsResponseSchema = z.object({
    settings: ChatbotSettingsSchema,
    effective: z.object({
        provider: z.enum(['anthropic', 'bedrock']),
        /** 설정이 null 일 때 쓰이는 배포 기본 모델 ID */
        envDefaultModelId: z.string(),
        /** 설정 반영 후 실제 사용될 모델 ID */
        modelId: z.string(),
        /** 티어별 실제 모델 ID (설정 화면 표시용) */
        modelIdsByTier: z.record(ChatbotModelTierSchema, z.string()),
    }),
    tools: z.array(AdminChatbotToolInfoSchema),
    updatedAt: z.string().nullable(),
});
/** GET /api/admin/chatbot-sessions/stats — 기간 내 KPI */
export const ChatbotSessionStatsSchema = z.object({
    range: z.object({ startDate: z.string().nullable(), endDate: z.string().nullable() }),
    sessions: z.number().int(),
    userMessages: z.number().int(),
    llmTurns: z.number().int(),
    faqAnswers: z.number().int(),
    errors: z.number().int(),
    escalated: z.number().int(),
    /** 0~1 */
    escalationRate: z.number(),
    inquiryCreated: z.number().int(),
    /** 0~1 */
    handoffRate: z.number(),
    actionExecuted: z.number().int(),
    /** 문의 전환·에스컬레이션 제안 없이 끝난 세션 수(추정) */
    selfResolvedEstimate: z.number().int(),
    tokens: z.object({ input: z.number().int(), output: z.number().int(), cacheRead: z.number().int() }),
});
//# sourceMappingURL=chatbot.js.map